import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPlus, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  fetchProductById,
  fetchProductMeters,
  fetchMeterAssignmentData,
  addMeterToProduct,
  clearError,
  clearSuccessMessage
} from '../../../store/slices/inventorySlice';
import { toast } from 'react-toastify';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import Table from '../../../components/Common/Table/Table';
import ListScreenSearchBar from '../../../components/Common/ListScreenSearchBar/ListScreenSearchBar';
import ListScreenPaginationBar from '../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import '../../../components/Common/ListScreenPageLayout/listScreenPage.css';
import './InventoryDetails.css';
import {
  getForaCareMeterSerialError,
  normalizeForaCareMeterExtensionForSubmit
} from '../../../utils/foraCareMeterUtils';

const emptyMeterAssignmentRow = () => ({
  practice_id: '',
  patient_id: '',
  extension: '',
  serial_number: '',
  gateway_serial_number: ''
});

const InventoryDetails = () => {
  const LEGACY_EXTENSION_OPTIONS = [
    { value: '1', label: 'User 1' },
    { value: '2', label: 'User 2' },
    { value: '3', label: 'User 3' },
    { value: '4', label: 'User 4' },
    { value: 'A', label: 'All' }
  ];
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { productId } = useParams();
  const { updateBreadcrumbs } = useBreadcrumb();
  const {
    currentProduct,
    meters,
    metersPagination,
    isProductDetailLoading,
    metersLoading,
    error,
    successMessage,
    meterAssignmentData
  } = useSelector((state) => state.inventory);

  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState('10');
  const [showAddMeterModal, setShowAddMeterModal] = useState(false);
  const [meterRows, setMeterRows] = useState([emptyMeterAssignmentRow()]);
  const [showError, setShowError] = useState(false);
  const [meterSaveLoading, setMeterSaveLoading] = useState(false);
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('asc');

  const listLimit = entriesPerPage === 'all' ? 9999 : parseInt(entriesPerPage, 10);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        setSearchTerm(localSearchTerm);
        setCurrentPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearchTerm, searchTerm]);

  const patientsForPractice = useCallback(
    (practiceId) => {
      if (!practiceId || !meterAssignmentData?.patients?.length) return [];
      const pid = Number(practiceId);
      return meterAssignmentData.patients.filter((p) => Number(p.practice_id) === pid);
    },
    [meterAssignmentData?.patients]
  );

  const rowHasAnyData = (row) =>
    !!(
      row.practice_id ||
      row.patient_id ||
      row.extension ||
      (row.serial_number || '').trim() ||
      (row.gateway_serial_number || '').trim()
    );

  const isRowComplete = (row) =>
    !!(
      row.practice_id &&
      row.patient_id &&
      row.extension &&
      (row.serial_number || '').trim()
    );

  const handleAddRow = (rowIndex) => {
    const currentRow = meterRows[rowIndex];
    if (!isRowComplete(currentRow)) {
      setShowError(true);
      return;
    }
    setShowError(false);
    setMeterRows([...meterRows, emptyMeterAssignmentRow()]);
  };

  const handleDeleteRow = (index) => {
    const next = meterRows.filter((_, i) => i !== index);
    setMeterRows(next.length ? next : [emptyMeterAssignmentRow()]);
  };

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...meterRows];
    const prev = updatedRows[index];
    if (field === 'practice_id') {
      updatedRows[index] = {
        ...prev,
        practice_id: value,
        patient_id: '',
        extension: ''
      };
    } else {
      updatedRows[index] = { ...prev, [field]: value };
    }
    setMeterRows(updatedRows);
    if (showError) setShowError(false);
  };

  const handleSaveMeters = async () => {
    const rowsToSave = meterRows.filter(rowHasAnyData);
    if (rowsToSave.length === 0) {
      toast.warning('Add at least one meter row with all required fields.');
      setShowError(true);
      return;
    }
    const incomplete = rowsToSave.filter((r) => !isRowComplete(r));
    if (incomplete.length) {
      setShowError(true);
      toast.error('Complete Practice, Patient, Extension, and Serial number for each row you started.');
      return;
    }
    for (const row of rowsToSave) {
      const serialErr = getForaCareMeterSerialError(row.serial_number);
      if (serialErr) {
        toast.error(serialErr);
        return;
      }
    }
    setMeterSaveLoading(true);
    setShowError(false);
    try {
      for (const row of rowsToSave) {
        await dispatch(
          addMeterToProduct({
            productId,
            meterData: {
              practice_id: row.practice_id,
              patient_id: row.patient_id,
              extension: normalizeForaCareMeterExtensionForSubmit(row.extension),
              serial_number: String(row.serial_number || '').trim(),
              gateway_serial_number: (row.gateway_serial_number || '').trim() || undefined
            }
          })
        ).unwrap();
        dispatch(clearSuccessMessage());
      }
      toast.success(`Saved ${rowsToSave.length} meter${rowsToSave.length === 1 ? '' : 's'}.`);
      setShowAddMeterModal(false);
      setMeterRows([emptyMeterAssignmentRow()]);
      refetchMeters();
    } catch (err) {
      // Error toast is shown once via Redux `error` + useEffect below (avoids duplicate toasts).
      void err;
    } finally {
      setMeterSaveLoading(false);
    }
  };

  useEffect(() => {
    if (productId) {
      dispatch(fetchProductById(productId)).catch((err) => {
        console.error('Error fetching product:', err);
      });
      dispatch(fetchMeterAssignmentData({ product_id: productId }));
    }
  }, [dispatch, productId]);

  useEffect(() => {
    if (showAddMeterModal) {
      dispatch(fetchMeterAssignmentData({ product_id: productId }));
    }
  }, [showAddMeterModal, dispatch]);

  useEffect(() => {
    if (currentProduct) {
      updateBreadcrumbs([
        { label: 'Inventory', path: '/inventory' },
        { label: currentProduct.product_name || 'Product Details', path: `/inventory/${productId}` }
      ]);
    } else {
      updateBreadcrumbs([
        { label: 'Inventory', path: '/inventory' },
        { label: 'Loading...', path: `/inventory/${productId}` }
      ]);
    }
  }, [currentProduct, productId, updateBreadcrumbs]);

  useEffect(() => {
    if (productId) {
      dispatch(
        fetchProductMeters({
          productId,
          params: {
            page: currentPage,
            limit: listLimit,
            search: searchTerm,
            sortBy,
            sortOrder
          }
        })
      );
    }
  }, [dispatch, productId, currentPage, listLimit, searchTerm, sortBy, sortOrder]);

  useEffect(() => {
    if (metersPagination?.total_pages && currentPage > metersPagination.total_pages) {
      setCurrentPage(Math.max(1, metersPagination.total_pages));
    }
  }, [metersPagination?.total_pages, currentPage]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
    if (successMessage) {
      toast.success(successMessage);
      dispatch(clearSuccessMessage());
    }
  }, [error, successMessage, dispatch]);

  const handlePageChange = (page) => {
    const maxPages = Math.max(1, metersPagination?.total_pages ?? 1);
    if (page >= 1 && page <= maxPages) {
      setCurrentPage(page);
    }
  };

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const refetchMeters = useCallback(() => {
    if (!productId) return;
    dispatch(
      fetchProductMeters({
        productId,
        params: {
          page: currentPage,
          limit: listLimit,
          search: searchTerm,
          sortBy,
          sortOrder
        }
      })
    );
  }, [dispatch, productId, currentPage, listLimit, searchTerm, sortBy, sortOrder]);

  const handleCloseAddMeterModal = () => {
    setShowAddMeterModal(false);
    setMeterRows([emptyMeterAssignmentRow()]);
    setShowError(false);
    refetchMeters();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    if (typeof dateString === 'string' && dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return dateString;
    }
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
      }
    } catch (err) {
      console.error('Error parsing date:', err);
    }
    return dateString || 'N/A';
  };

  const formatPrice = (price) => `$${parseFloat(price || 0).toFixed(2)}`;

  const handleSort = useCallback(
    (key) => {
      setCurrentPage(1);
      if (sortBy === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      else {
        setSortBy(key);
        setSortOrder('asc');
      }
    },
    [sortBy]
  );

  const meterColumns = useMemo(
    () => [
      { key: 'id', title: '#', sortable: true, className: 'reusable-table-centered-col', render: (row) => row.id },
      { key: 'product_name', title: 'Product Name', sortable: true, render: (row) => row.product_name || 'N/A' },
      {
        key: 'product_model_number',
        title: 'Product Model number',
        sortable: true,
        render: (row) => row.product_model_number || 'N/A'
      },
      {
        key: 'serial_number',
        title: 'Product Serial number',
        sortable: true,
        render: (row) => row.serial_number || 'N/A'
      },
      { key: 'assignee_name', title: 'Assignee', sortable: true, render: (row) => row.assignee_name || 'N/A' },
      { key: 'assigning_date', title: 'Assigning Date', sortable: true, render: (row) => row.assigning_date || 'N/A' }
    ],
    []
  );

  const availableSerialNumbers = useMemo(() => {
    const list = meterAssignmentData?.serialNumbers || [];
    return [...list].sort((a, b) => {
      const sa = String(a?.serial_number ?? '');
      const sb = String(b?.serial_number ?? '');
      if (sa.length !== sb.length) return sa.length - sb.length;
      return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [meterAssignmentData?.serialNumbers]);

  const entriesSelectValue = entriesPerPage === 'all' ? 'all' : String(entriesPerPage);
  const totalMeterPages = entriesPerPage === 'all' ? 1 : Math.max(1, metersPagination?.total_pages ?? 1);

  if (isProductDetailLoading) {
    return (
      <div className="inventory-details-container app-page-loader">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error && !currentProduct) {
    return (
      <div className="inventory-details-container">
        <div className="alert alert-danger" role="alert">
          {error || 'Product not found'}
        </div>
        <button type="button" className="btn btn-primary mt-3" onClick={() => navigate('/inventory')}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Inventory List
        </button>
      </div>
    );
  }

  if (!currentProduct && !isProductDetailLoading) {
    return (
      <div className="inventory-details-container">
        <div className="alert alert-warning" role="alert">
          Product not found. Please check the product ID.
        </div>
        <button type="button" className="btn btn-primary mt-3" onClick={() => navigate('/inventory')}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Inventory List
        </button>
      </div>
    );
  }

  return (
    <div className="inventory-details-container">
      <div className="inventory-details-header">
        <div>
          <h3 className="inventory-details-title">Inventory Details</h3>
        </div>
        <button type="button" className="inventory-details-navy-btn" onClick={() => navigate('/inventory')}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          List
        </button>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="inventory-details-card">
            <div className="inventory-details-card-header">
              <h5 className="inventory-details-card-title mb-0">Inventory Details</h5>
            </div>
            <div className="inventory-details-card-body">
              <div className="row mb-4">
                <div className="col-md-6">
                  <div className="inventory-details-info-row">
                    <label>Product Category</label>
                    <span>{currentProduct.product_category || 'N/A'}</span>
                  </div>
                  <div className="inventory-details-info-row">
                    <label>Product Name</label>
                    <span>{currentProduct.product_name || 'N/A'}</span>
                  </div>
                  <div className="inventory-details-info-row">
                    <label>Meter model</label>
                    <span>{currentProduct.model_number || 'N/A'}</span>
                  </div>
                  <div className="inventory-details-info-row">
                    <label>Received Date</label>
                    <span>{formatDate(currentProduct.received_date) || 'N/A'}</span>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="inventory-details-info-row">
                    <label>Price</label>
                    <span>{formatPrice(currentProduct.price)}</span>
                  </div>
                  <div className="inventory-details-info-row">
                    <label>Qty of Each Category</label>
                    <span>{currentProduct.qty || 'N/A'}</span>
                  </div>
                  <div className="inventory-details-info-row">
                    <label>Status</label>
                    <span>
                      {currentProduct.status === 'Active' ? (
                        <span className="inventory-details-badge inventory-details-badge-active">Active</span>
                      ) : currentProduct.status === 'Inactive' ? (
                        <span className="inventory-details-badge inventory-details-badge-inactive">Inactive</span>
                      ) : (
                        <span className="inventory-details-badge inventory-details-badge-deactive">Deactive</span>
                      )}
                    </span>
                  </div>
                  <div className="inventory-details-info-row">
                    <label>Gateway Serial Number</label>
                    <span>{currentProduct.gateway_serial_number || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-12">
          <div className="inventory-details-card inventory-details-card--mapped">
            <div className="inventory-details-card-header">
              <h5 className="inventory-details-card-title mb-0">Mapped device</h5>
            </div>
            <div className="inventory-details-card-body inventory-details-card-body--table">
              <div className="list-screen-actions inventory-details-mapped-actions">
                <ListScreenSearchBar
                  value={localSearchTerm}
                  onChange={setLocalSearchTerm}
                  isLoading={metersLoading || localSearchTerm !== searchTerm}
                  inputId="inventory-details-meters-search"
                />
                <button type="button" className="list-screen-add-btn" onClick={() => setShowAddMeterModal(true)}>
                  <FontAwesomeIcon icon={faPlus} className="list-screen-add-icon" />
                  Add new meter
                </button>
              </div>
              <Table
                columns={meterColumns}
                data={meters}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
                isLoading={metersLoading}
                loadingMode="skeleton"
                skeletonRows={8}
                emptyMessage="No data available in table"
              />
              <ListScreenPaginationBar
                entriesValue={entriesSelectValue}
                onEntriesChange={handleEntriesChange}
                currentPage={metersPagination?.current_page ?? currentPage}
                totalPages={totalMeterPages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </div>
      </div>

      {showAddMeterModal && (
        <div className="inventory-details-modal-overlay">
          <div className="inventory-details-modal-content inventory-details-modal-content--add-meter">
            <div className="inventory-details-modal-header">
              <h5 className="inventory-details-modal-title">Meter : {currentProduct?.product_name}</h5>
              <button type="button" className="inventory-details-modal-close" onClick={handleCloseAddMeterModal}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="inventory-details-modal-body">
              <div className="meter-rows-container">
                <div className="inventory-details-meter-model-banner">
                  <span className="inventory-details-meter-model-banner-label">Meter model</span>
                  <span className="inventory-details-meter-model-banner-value">
                    {currentProduct?.model_number || 'N/A'}
                  </span>
                </div>

                <div className="inventory-details-meter-form-grid inventory-details-meter-form-grid--header">
                  <div className="inventory-details-meter-field">
                    <label className="form-label mb-0">
                      Practice <span className="text-danger">*</span>
                    </label>
                  </div>
                  <div className="inventory-details-meter-field">
                    <label className="form-label mb-0">
                      Patient <span className="text-danger">*</span>
                    </label>
                  </div>
                  <div className="inventory-details-meter-field">
                    <label className="form-label mb-0">
                      Extension <span className="text-danger">*</span>
                    </label>
                  </div>
                  <div className="inventory-details-meter-field">
                    <label className="form-label mb-0">
                      Serial number <span className="text-danger">*</span>
                    </label>
                  </div>
                  <div className="inventory-details-meter-field">
                    <label className="form-label mb-0">Gateway serial number</label>
                  </div>
                  <div className="inventory-details-meter-field inventory-details-meter-field--action" aria-hidden="true" />
                </div>

                {meterRows.map((row, index) => (
                  <div className="inventory-details-meter-form-grid inventory-details-meter-form-grid--row" key={index}>
                    <div className="inventory-details-meter-field">
                      <select
                        className="form-select"
                        value={row.practice_id}
                        onChange={(e) => handleInputChange(index, 'practice_id', e.target.value)}
                      >
                        <option value="">Select Practice</option>
                        {(meterAssignmentData?.practices || []).map((p) => (
                          <option key={p.id} value={String(p.id)}>
                            {p.practice_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="inventory-details-meter-field">
                      <select
                        className="form-select"
                        value={row.patient_id}
                        onChange={(e) => handleInputChange(index, 'patient_id', e.target.value)}
                        disabled={!row.practice_id}
                      >
                        <option value="">{row.practice_id ? 'Select Patient' : 'Select practice first'}</option>
                        {patientsForPractice(row.practice_id).map((patient) => (
                          <option key={patient.id} value={String(patient.id)}>
                            {patient.patient_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="inventory-details-meter-field inventory-details-meter-extension-cell">
                      <select
                        className="form-select"
                        value={row.extension}
                        onChange={(e) => handleInputChange(index, 'extension', e.target.value)}
                      >
                        <option value="">Select extension</option>
                        {LEGACY_EXTENSION_OPTIONS.map((ext) => (
                          <option key={ext.value} value={ext.value}>
                            {ext.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="inventory-details-meter-field">
                      <select
                        className="form-select"
                        value={row.serial_number}
                        onChange={(e) => handleInputChange(index, 'serial_number', e.target.value)}
                      >
                        <option value="">Select serial number</option>
                        {(availableSerialNumbers).map((item, idx) => (
                          <option key={`${item.serial_number}-${idx}`} value={item.serial_number}>
                            {item.serial_number}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="inventory-details-meter-field">
                      <input
                        type="text"
                        className="form-control"
                        value={row.gateway_serial_number}
                        onChange={(e) => handleInputChange(index, 'gateway_serial_number', e.target.value)}
                        placeholder="Optional"
                      />
                    </div>
                    <div className="inventory-details-meter-field inventory-details-meter-field--action">
                      {index === meterRows.length - 1 ? (
                        <button
                          type="button"
                          className="inventory-details-meter-row-btn inventory-details-meter-row-btn--add"
                          onClick={() => handleAddRow(index)}
                          title="Add row"
                        >
                          <FontAwesomeIcon icon={faPlus} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="inventory-details-meter-row-btn inventory-details-meter-row-btn--remove"
                          onClick={() => handleDeleteRow(index)}
                          title="Remove row"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {showError && (
                  <div className="inventory-details-meter-form-error">
                    <p className="text-danger mb-0">
                      Fill the mandatory fields: Practice, Patient, Extension, and Serial number.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="inventory-details-modal-footer">
              <button type="button" className="inventory-details-modal-cancel-btn" onClick={handleCloseAddMeterModal}>
                Cancel
              </button>
              <button
                type="button"
                className="inventory-details-modal-save-btn"
                disabled={meterSaveLoading}
                onClick={handleSaveMeters}
              >
                {meterSaveLoading ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryDetails;
